import Image from 'next/future/image';
import React from 'react';
import cn from 'classnames';
import blackTick from '@/public/icons/account/blackTick.svg';
import { formatDayJsDateShort } from '@/components/track-your-order/utils';
import { FulfillmentStatusBarProps } from '@/lib/smartForm/types';

const MobileShippingProgress = (props: {
  title: string;
  stage: number;
  activeStage: number;
  date: string;
}) => {
  const { title, stage, activeStage, date } = props;

  return (
    <div className="relative z-20 mt-5 flex w-full items-center text-sm first:mt-0 xxs:mt-6 xxs:text-base">
      <div
        className={cn(
          'flex h-[32px] w-[32px] flex-none items-center justify-center rounded-full border-2 border-black bg-white text-center  text-sm font-bold !leading-none text-gray-700 xxs:h-[35px] xxs:w-[35px] xxs:text-base',
          {
            '!border-green-set-800 text-green-set-800': stage <= activeStage,
          },
        )}
      >
        <span className="mt-1">{stage}</span>
      </div>
      <div className="flex w-full justify-between">
        <div
          className={cn('whitespace-nowrap pl-3 text-black xxs:pl-4', {
            'font-normal': stage < activeStage,
            'font-bold': stage <= activeStage,
          })}
        >
          {title}
        </div>
        <div
          className={cn('text-black', {
            'font-normal': stage < activeStage,
            'font-bold': stage <= activeStage,
          })}
        >
          {date}
        </div>
      </div>
    </div>
  );
};

const DesktopShippingProgressDot = (props: {
  stage: number;
  activeStage: number;
}) => {
  const { stage, activeStage } = props;

  return (
    <div
      className={cn(
        'z-20 mt-[-1px] h-[15px] w-[15px] rounded-full border border-gray-700',
        { 'bg-green-set-800': stage <= activeStage },
      )}
    />
  );
};

const DesktopShippingProgressInfo = (props: {
  info: string;
  stage: number;
  activeStage: number;
}) => {
  const { info, stage, activeStage } = props;

  return (
    <div
      className={cn('w-[100px] text-center', {
        'font-bold': stage === activeStage,
        'font-normal': stage < activeStage,
      })}
    >
      {info}
    </div>
  );
};

export default function FulfillmentStatusBar({
  status,
  isRefunded,
  orderedDate,
  dispatchedDate,
  outForDeliveryDate,
  deliveredDate,
  canceledAt,
  storeTimeZone,
  isHelpPage,
}: FulfillmentStatusBarProps) {
  const singleLineComponent = (line: string) => (
    <div className="flex w-full items-center justify-center bg-green-set-200 py-10 text-center text-sm sm:py-7">
      <div className="mr-1 flex h-[15px] w-[15px] items-center justify-center">
        <Image width={15} height={15} alt="tick" src={blackTick} />
      </div>
      <span className="font-bold">{line}</span>
    </div>
  );
  const formattedOrderedDate = formatDayJsDateShort(orderedDate, storeTimeZone);
  const formattedDispatchDate = formatDayJsDateShort(
    dispatchedDate || '',
    storeTimeZone,
  );
  const formattedOutForDeliveryDate = formatDayJsDateShort(
    outForDeliveryDate || '',
    storeTimeZone,
  );
  const formattedCanceledDate = formatDayJsDateShort(
    canceledAt || '',
    storeTimeZone,
  );

  if (canceledAt != null)
    return singleLineComponent(`Cancelled on ${formattedCanceledDate}`);

  if (isRefunded) return singleLineComponent(`Item has been refunded`);

  if (status === 'delivered') {
    const statusComment =
      deliveredDate !== undefined
        ? `Delivered on ${deliveredDate}`
        : 'Delivered';

    return singleLineComponent(statusComment);
  }

  const statusStageMap: Record<string, number> = {
    ordered: 1,
    dispatched: 2,
    outForDelivery: 3,
    delivered: 4,
  };
  const progressWidthMap: Record<number, string> = {
    1: 'w-[0%]',
    2: 'w-[33%]',
    3: 'w-[66%]',
    4: 'w-[99%]',
  };

  const activeStage = statusStageMap[status];

  return (
    <div
      className={cn(
        'flex w-full flex-wrap bg-green-set-500 py-5 px-4 xxs:py-7 sm:justify-center sm:bg-green-set-200 sm:py-3.5 sm:px-0',
        {
          'flex-col px-2 font-bold': isHelpPage && activeStage >= 2,
        },
      )}
    >
      {isHelpPage && activeStage >= 2 && (
        <div className="px-6 py-2">Your order is on its way.</div>
      )}
      <div
        className={cn(
          'mb-1 hidden  justify-between text-[11px] sm:flex',
          { 'w-[96%] self-center': isHelpPage },
          { 'w-[90%]': !isHelpPage },
        )}
      >
        <DesktopShippingProgressInfo
          info={formattedOrderedDate}
          stage={1}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info={formattedDispatchDate}
          stage={2}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info={formattedOutForDeliveryDate}
          stage={3}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info={deliveredDate || ''}
          stage={4}
          activeStage={activeStage}
        />
      </div>
      <div
        className={cn(
          'relative hidden h-[15px] justify-between rounded-lg border border-gray-200 bg-white sm:flex',
          { 'w-[92%] self-center': isHelpPage },
          { 'relative w-[85%] xl:w-[75%]': !isHelpPage },
        )}
      >
        <div
          className={`absolute left-[2px] top-[1px] z-10 h-[12px] ${progressWidthMap[activeStage]} rounded-lg bg-green-set-800`}
        />
        <DesktopShippingProgressDot stage={1} activeStage={activeStage} />
        <DesktopShippingProgressDot stage={2} activeStage={activeStage} />
        <DesktopShippingProgressDot stage={3} activeStage={activeStage} />
        <DesktopShippingProgressDot stage={4} activeStage={activeStage} />
      </div>
      <div
        className={cn(
          'mt-1.5 hidden justify-between text-[11px] sm:flex',
          { 'w-[100%]': isHelpPage },
          { 'w-[90%]': !isHelpPage },
        )}
      >
        <DesktopShippingProgressInfo
          info="Ordered"
          stage={1}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info="Dispatched"
          stage={2}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info="Out For Delivery"
          stage={3}
          activeStage={activeStage}
        />
        <DesktopShippingProgressInfo
          info="Delivered"
          stage={4}
          activeStage={activeStage}
        />
      </div>
      <div className="relative block w-full sm:hidden">
        <MobileShippingProgress
          title="Order received"
          stage={1}
          activeStage={activeStage}
          date={formattedOrderedDate}
        />
        <MobileShippingProgress
          title="Dispatch In Process"
          stage={2}
          activeStage={activeStage}
          date={formattedDispatchDate}
        />
        <MobileShippingProgress
          title="Out For Delivery"
          stage={3}
          activeStage={activeStage}
          date={formattedOutForDeliveryDate}
        />
        <MobileShippingProgress
          title="Delivered"
          stage={4}
          activeStage={activeStage}
          date={deliveredDate || ''}
        />
        {/* eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values */}
        <div className="absolute left-[0px] top-1/2 z-10 h-[90%] w-[32px] -translate-y-1/2 border-x-2 border-[#C7EDDC] bg-[#D9F8EA] xxs:w-[35px]"></div>
      </div>
    </div>
  );
}
